import { error as logError } from "./console";

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

/** getData/postData may return this shape instead of throwing when aborted. */
export function isCancelledEventTimesResponse(result: unknown): boolean {
  if (!result || typeof result !== "object") return false;
  const response = result as { type?: string; error?: string };
  return response.type === "AbortError" || response.error === "Request cancelled";
}

export interface RunEventTimesSelectionOptions {
  fetchTimes: () => Promise<unknown>;
  applySelection: (result: unknown) => void;
  /** Optional prefix, e.g. `ManeuverWindow` → `ManeuverWindow: Error fetching times:` */
  logPrefix?: string;
  /** Defaults to `Error fetching times:` */
  fetchErrorLabel?: string;
  /** Defaults to `Error applying event times selection:` */
  applyErrorLabel?: string;
}

function formatLogMessage(prefix: string | undefined, label: string): string {
  return prefix ? `${prefix}: ${label}` : label;
}

/**
 * Fetch maneuver event times and apply selection in separate try/catch blocks so
 * render/store errors from applySelection are not misreported as fetch failures.
 */
export async function runEventTimesSelectionSync(
  options: RunEventTimesSelectionOptions
): Promise<void> {
  const fetchLabel = options.fetchErrorLabel ?? "Error fetching times:";
  const applyLabel = options.applyErrorLabel ?? "Error applying event times selection:";

  let result: unknown;
  try {
    result = await options.fetchTimes();
  } catch (error: unknown) {
    if (!isAbortError(error)) {
      logError(formatLogMessage(options.logPrefix, fetchLabel), error);
    }
    return;
  }

  try {
    options.applySelection(result);
  } catch (error: unknown) {
    logError(formatLogMessage(options.logPrefix, applyLabel), error);
  }
}
